<template>
    <v-dialog
        v-model="dialog"
        persistent
        max-width="900"
        scrollable
        :hide-overlay="false"
        content-class="modal-impeditivo-ponto"
    >
        <v-card v-if="loading">
            <v-card-text class="text-center pa-8">
                <v-progress-circular indeterminate color="primary" size="64" />
                <p class="mt-4 text-body-1">Verificando pendências...</p>
            </v-card-text>
        </v-card>

        <v-card v-else-if="currentTerm">
            <v-toolbar dark color="warning">
                <v-icon class="mr-2">mdi-alert-circle</v-icon>
                <v-toolbar-title>Pendência de Termo Obrigatório</v-toolbar-title>
                <v-spacer />
                <v-chip small dark class="mr-2" v-if="pendingTerms.length > 1">
                    {{ currentIndex + 1 }} / {{ pendingTerms.length }}
                </v-chip>
            </v-toolbar>

            <v-card-text class="pa-0">
                <!-- Aviso de bloqueio -->
                <v-alert type="warning" dense class="ma-4 mb-0" border="left">
                    <strong>Atenção:</strong> Você possui termo(s) obrigatório(s) pendente(s) de assinatura.
                    O acesso ao registro de ponto permanece bloqueado até a assinatura. Assine abaixo para liberar imediatamente.
                </v-alert>

                <!-- Informações do termo -->
                <v-card flat class="ma-4">
                    <v-card-title class="text-h6 pb-2">
                        <v-icon left color="primary">mdi-file-document-outline</v-icon>
                        {{ currentTerm.term.name }}
                    </v-card-title>
                    <v-card-subtitle v-if="currentTerm.term.description">
                        {{ currentTerm.term.description }}
                    </v-card-subtitle>
                </v-card>

                <!-- Conteúdo do termo -->
                <v-divider />
                <div class="pa-4 term-content" style="max-height: 400px; overflow-y: auto;">
                    <div v-if="termContent" v-html="termContent"></div>
                    <v-skeleton-loader v-else type="paragraph@3" />
                </div>
                <v-divider />

                <!-- Área de aceite -->
                <v-card flat class="ma-4 grey lighten-4">
                    <v-card-title class="text-subtitle-1 pb-2">
                        <v-icon left color="primary" small>mdi-file-sign</v-icon>
                        Confirmação de Aceite
                    </v-card-title>
                    <v-card-text>
                        <!-- Prioridade: term_options > is_required checkbox > institution.term_options -->
                        
                        <!-- Caso 1: Termo tem opções de resposta -->
                        <v-radio-group
                            v-if="termOptions.length > 0"
                            v-model="formOptionId"
                            hide-details
                            class="mt-0"
                            @change="onOptionChange"
                        >
                            <v-radio
                                v-for="opt in termOptions"
                                :key="opt.id"
                                :label="opt.label"
                                :value="opt.id"
                                :color="opt.color || (opt.is_valid_acceptance ? 'success' : 'grey')"
                            />
                        </v-radio-group>
                        
                        <!-- Caso 2: Termo obrigatório sem opções (checkbox legado) -->
                        <v-checkbox
                            v-else-if="currentTerm.term.is_required"
                            v-model="formAgree"
                            :label="currentTerm.term.label || 'Li e concordo com os termos acima'"
                            color="primary"
                            hide-details
                            class="mt-0"
                        />
                        
                        <!-- Campo de Justificativa Condicional -->
                        <v-textarea
                            v-if="showJustificationField"
                            v-model="formJustification"
                            label="Justificativa *"
                            :counter="2000"
                            :rules="justificationRules"
                            rows="3"
                            outlined
                            class="mt-4"
                            persistent-placeholder
                            placeholder="Informe o motivo da sua decisão..."
                            hint="A justificativa é obrigatória para a opção selecionada (mínimo 10 caracteres)"
                            persistent-hint
                        />
                    </v-card-text>
                </v-card>
            </v-card-text>

            <v-divider />

            <v-card-actions class="pa-4">
                <v-spacer />
                <v-btn
                    color="primary"
                    :loading="submitting"
                    :disabled="!canSubmit"
                    @click="signTerm"
                    large
                >
                    <v-icon left>mdi-check</v-icon>
                    <span>
                        {{ submitButtonLabel }}
                    </span>
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Alert para mensagens -->
        <emc-alert-snackbar :show="!!message.length" :message="message" :color="error ? 'error' : 'success'" />
    </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'

export default {
    name: 'SysTermPointBlockingModal',

    props: {
        show: {
            type: Boolean,
            default: false
        },
        teacherId: {
            type: Number,
            required: true
        },
        token: {
            type: String,
            default: null
        },
        /** Termos já carregados pelo parent - evita nova requisição e exibe modal imediatamente */
        initialTerms: {
            type: Array,
            default: null
        }
    },

    data: () => ({
        dialog: false,
        loading: false,
        submitting: false,
        error: false,
        message: '',
        pendingTerms: [],
        currentIndex: 0,
        termContent: null,
        formAgree: false,
        formOptionId: null,
        formJustification: '',
        justificationRules: [
            v => !v || v.length >= 10 || 'A justificativa deve ter pelo menos 10 caracteres',
            v => !v || v.length <= 2000 || 'A justificativa não pode ter mais de 2000 caracteres'
        ]
    }),

    computed: {
        submitButtonLabel() {
            const hasMoreThanOne = Array.isArray(this.pendingTerms) && this.pendingTerms.length > 1
            const hasNext = typeof this.currentIndex === 'number' && this.currentIndex < this.pendingTerms.length - 1
            return hasMoreThanOne && hasNext ? 'Assinar e Continuar' : 'Assinar e Liberar Acesso'
        }
    },

    computed: {
        currentTerm() {
            return this.pendingTerms[this.currentIndex] || null
        },

        /**
         * Opções de resposta do termo (prioridade: termo > instituição)
         */
        termOptions() {
            if (!this.currentTerm) {
                return []
            }
            
            // Prioridade 1: Opções específicas do termo
            const termOpts = this.currentTerm.term?.term_options || this.currentTerm.term?.termOptions
            if (termOpts && termOpts.length > 0) {
                return termOpts.filter(opt => opt.active !== false)
            }
            
            // Prioridade 2: Opções da instituição (fallback legado)
            const instOpts = this.currentTerm.institution?.term_options || this.currentTerm.institution?.termOptions
            if (instOpts && instOpts.length > 0) {
                return instOpts.filter(opt => opt.active !== false)
            }
            
            return []
        },

        /**
         * Retorna a opção selecionada atualmente
         */
        selectedOption() {
            if (!this.formOptionId || this.termOptions.length === 0) {
                return null
            }
            return this.termOptions.find(opt => opt.id === this.formOptionId) || null
        },

        /**
         * Verifica se deve exibir o campo de justificativa
         */
        showJustificationField() {
            if (!this.selectedOption) {
                return false
            }
            return this.selectedOption.require_justification === true || 
                   this.selectedOption.require_justification === 1
        },

        /**
         * Verifica se a justificativa é válida (quando obrigatória)
         */
        isJustificationValid() {
            if (!this.showJustificationField) {
                return true
            }
            const justification = this.formJustification?.trim() || ''
            return justification.length >= 10 && justification.length <= 2000
        },

        canSubmit() {
            if (!this.currentTerm) return false

            // Caso 1: Termo com opções de resposta
            if (this.termOptions.length > 0) {
                if (this.formOptionId === null) return false
                if (this.showJustificationField && !this.isJustificationValid) return false
                return true
            }

            // Caso 2: Termo obrigatório (checkbox legado)
            if (this.currentTerm.term.is_required) {
                return this.formAgree === true
            }

            return false
        }
    },

    watch: {
        show(val) {
            if (val) {
                this.checkBlockingTerms()
            }
        },

        dialog(val) {
            this.$emit('update:show', val)
        },

        currentTerm: {
            handler(term) {
                if (term) {
                    this.loadTermContent()
                    this.resetForm()
                }
            },
            immediate: true
        }
    },

    methods: {
        ...mapActions('term', ['ActionGetPendingBlockingTerms', 'ActionShowTermSignatureByTeacher', 'ActionSignByTeacherOwner']),

        async checkBlockingTerms() {
            if (this.initialTerms && this.initialTerms.length > 0) {
                this.pendingTerms = this.initialTerms
                this.currentIndex = 0
                this.loading = false
                this.dialog = true
                return
            }

            this.loading = true
            this.pendingTerms = []
            this.currentIndex = 0

            try {
                const response = await this.ActionGetPendingBlockingTerms({
                    teacherId: this.teacherId,
                    token: this.token
                })

                const data = response.data || response
                if (data.has_blocking_terms && data.terms && data.terms.length > 0) {
                    this.pendingTerms = data.terms
                    this.dialog = true
                } else {
                    this.dialog = false
                    this.$emit('unblocked')
                }
            } catch (err) {
                console.error('Erro ao verificar termos bloqueantes:', err)
                this.dialog = false
                this.$emit('unblocked')
            } finally {
                this.loading = false
            }
        },

        async loadTermContent() {
            if (!this.currentTerm) return

            this.termContent = null

            try {
                const response = await this.ActionShowTermSignatureByTeacher({
                    id: this.currentTerm.id
                })

                const data = response.data || response
                this.termContent = data.content || this.currentTerm.term.content
            } catch (err) {
                console.error('Erro ao carregar conteúdo do termo:', err)
                this.termContent = this.currentTerm.term.content
            }
        },

        resetForm() {
            this.formAgree = false
            this.formOptionId = null
            this.formJustification = ''
            this.error = false
            this.message = ''
        },

        onOptionChange() {
            // Limpar justificativa quando trocar de opção
            this.formJustification = ''
        },

        async signTerm() {
            if (!this.canSubmit || !this.currentTerm) return

            // Validação de segurança: garantir que o ID existe
            const signatureId = this.currentTerm.id
            if (!signatureId) {
                this.error = true
                this.message = 'Erro: ID da assinatura não encontrado. Recarregue a página e tente novamente.'
                console.error('signTerm: currentTerm.id is null/undefined', this.currentTerm)
                return
            }

            this.submitting = true
            this.error = false
            this.message = ''

            try {
                const payload = {
                    id: signatureId,
                    agree: this.formAgree,
                    term_option_id: this.formOptionId,
                    justification: this.showJustificationField ? this.formJustification : null,
                    content: this.termContent
                }

                await this.ActionSignByTeacherOwner(payload)

                this.message = 'Termo assinado com sucesso!'

                // Verificar se há mais termos
                if (this.currentIndex < this.pendingTerms.length - 1) {
                    // Avançar para o próximo termo
                    this.currentIndex++
                } else {
                    // Todos os termos foram assinados - liberar acesso automaticamente
                    setTimeout(() => {
                        this.dialog = false
                        this.$emit('unblocked')
                    }, 800)
                }
            } catch (err) {
                console.error('Erro ao assinar termo:', err)
                console.error('Payload enviado:', { id: signatureId, currentTerm: this.currentTerm })
                this.error = true
                // Extrair mensagem de erro da resposta do servidor
                const errorMsg = err.response?.data?.message 
                    || err.body?.message
                    || err.response?.data?.error 
                    || err.message 
                    || 'Erro ao assinar o termo. Tente novamente.'
                this.message = errorMsg
            } finally {
                this.submitting = false
            }
        }
    }
}
</script>

<style scoped>
.term-content {
    font-size: 14px;
    line-height: 1.6;
}

.term-content >>> p {
    margin-bottom: 1em;
}

.term-content >>> ul,
.term-content >>> ol {
    margin-bottom: 1em;
    padding-left: 2em;
}
</style>
